import {
  INTEGRATOR_APP_FEATURE_CATEGORY,
  INTEGRATOR_APP_PROVIDER,
} from "@modules/app/service/app.constant";
import { mapActions, mapGetters, mapState } from "vuex";
import AuthoringCoPilotApi from "@apis/authoring.copilot.api";
import { defineAsyncComponent } from "vue";
import {
  NAMESPACE,
  COMPONENT_AUTHORING_COPILOT_TYPE,
  MAXIMUM_RESOURCE_TAGGINGS,
} from "@modules/editor/service/editor.constant";
import { isEmpty } from "@utils/var.check";
import resourceTaggingsStore from "@modules/editor/components/resource-taggings/ResourceTaggingsStore";
import {
  KB_VALIDATION_CONFIG,
  type KbFileError,
} from "@modules/editor/service/authoring.copilot.constant.ts";
import EditorErrorMixin from "@modules/editor/mixin/editor.error.mixin";
import ErrorMixin from "@plugins/common/mixin/error.mixin.ts";
import { type KnowledgeBase, KnowledgeBaseContentType } from "@plugins/authoring-copilot/types.ts";

/**
 * Mixin with utility functions for Authoring Co-Pilot
 */
export default {
  mixins: [EditorErrorMixin, ErrorMixin],
  data() {
    return {
      hasDirty: false,
      observer: null, // To check for resource tagging loading
      isResourceTaggingLoading: true,
      resourceTaggingsStore: resourceTaggingsStore,
      resourceTaggings: [],
      authoringCoPilotForm: {}, // Additional fields to be provided by components importing this mixin
      selectedRecommendationIds: [], // Used by GeneratedContentContainer to return list of selected Ids
      showExceedFilesError: false,
      isKnowledgeBaseContainPDF: false,
      isKnowledgeBaseContainGoogleDocument: false,
      kbValidationResult: { valid: true, errorType: null, fileErrors: [] },
    };
  },
  props: {
    authoringCoPilotAppCategory: { type: String, default: null }, // authoringCoPilotAppCategory can be provided via props or params.
  },
  computed: {
    ...mapState(NAMESPACE.LESSON, {
      lesson: (state) => state.lesson,
    }),
    ...mapGetters(NAMESPACE.LESSON, ["currentSection"]),
    ...mapGetters(NAMESPACE.APP, [
      "getIntegratorAppAccessByAppFeatureId",
      "getIntegratorAppAccessByCategory",
    ]),
    hasAuthoringCoPilotAppAccess() {
      // For V2R2, only Chatgpt can be used for ACP, hence this change. (No multi-modal) SLSUAT-5332
      return this.isChatGptAppAvailable;
    },
    aaAppsBySelectedCategory() {
      return this.getIntegratorAppAccessByCategory(this.appCategory);
    },
    isChatGptAppAvailable() {
      return this.isAaAppAvailable(INTEGRATOR_APP_PROVIDER.OPENAI);
    },
    isClaudeThreeAppAvailable() {
      return this.isAaAppAvailable(INTEGRATOR_APP_PROVIDER.ANTHROPIC);
    },
    appCategory() {
      return this.authoringCoPilotAppCategory
        ? this.authoringCoPilotAppCategory
        : this.params?.authoringCoPilotAppCategory;
    },
    resourceUuid() {
      // mixin shared among multiple layer of lesson hence, resource uuid can be lesson/section/question
      if (isEmpty(this.hasOriginalComponent) && !isEmpty(this.question)) return this.question.uuid;
      if (!isEmpty(this.sectionData)) return this.sectionData.uuid;
      if (!isEmpty(this.lesson)) return this.lesson.uuid;
      return "";
    },
    editingResourceTaggings() {
      return this.resourceTaggingsStore.getContentMapTaggingsByResource(this.resourceUuid);
    },
    subjectLevelTaggings() {
      return this.resourceTaggingsStore.getSubjectAndLevelTaggingsByResource(this.resourceUuid);
    },
    showLibraryOption() {
      return false;
    },
  },
  methods: {
    ...mapActions(NAMESPACE.LESSON, [
      "saveLessonResourceTaggings",
      "saveLessonActivityResourceTaggings",
    ]),
    handleSetDirty() {
      // Called when parent input fields change
      this.hasDirty = true;
      this.setDirty(this.dirtyId);
    },
    handleRemoveDirty() {
      // Called when discarded or saved parent input fields change
      this.hasDirty = false;
      this.removeDirty(this.dirtyId);
    },
    handleChildDirtyChanged(isChildDirty) {
      if (isChildDirty) {
        // If child is dirty
        this.setDirty(this.dirtyId); // Set parent as dirty
      } else if (!this.hasDirty) {
        // If both child and parent are not dirty
        this.removeDirty(this.dirtyId); // Remove parent dirty state
      }
    },
    getFirstSubjectAndLevel() {
      if (isEmpty(this.subjectLevelTaggings)) {
        return null;
      }
      return {
        subject: this.subjectLevelTaggings[0].subject,
        levelAndStream: this.subjectLevelTaggings[0].levelAndStream,
      };
    },
    populateResourceTaggingToSave() {
      const resourceTagging = {
        resourceTaggingSubjectLevelList: [],
        resourceTaggingContentMapList: [],
      };

      // clone contentMapTaggings
      const contentMapTaggings = this.editingResourceTaggings.map((x) => {
        return JSON.parse(JSON.stringify(x));
      });

      if (!isEmpty(contentMapTaggings)) {
        contentMapTaggings.forEach((contentMapTagging) => {
          if (isEmpty(contentMapTagging.contentMapMetadata)) {
            return;
          }
          delete contentMapTagging.contentMapMetadata.expired;
          delete contentMapTagging.contentMapMetadata.started;
          delete contentMapTagging.contentMapMetadata.viewableByTeachers;

          resourceTagging.resourceTaggingContentMapList.push({
            contentMapMetadata: contentMapTagging.contentMapMetadata,
            taggings: contentMapTagging.taggings,
          });
        });
      }

      // clone subjectLevelTaggings
      const subjectLevelTaggings = this.subjectLevelTaggings.map((x) => {
        return JSON.parse(JSON.stringify(x));
      });

      if (!isEmpty(subjectLevelTaggings)) {
        subjectLevelTaggings.forEach((subjectLevelTagging) => {
          if (
            !isEmpty(subjectLevelTagging.subject.id) &&
            !isEmpty(subjectLevelTagging.levelAndStream.id)
          ) {
            resourceTagging.resourceTaggingSubjectLevelList.push({
              subject: subjectLevelTagging.subject,
              levelAndStream: subjectLevelTagging.levelAndStream,
            });
          }
        });
      }

      this.resourceTaggings = resourceTagging;
    },
    validateNumberOfResourceTaggings(taggings) {
      let counter = 0;

      if (!isEmpty(taggings.resourceTaggingSubjectLevelList)) {
        counter += taggings.resourceTaggingSubjectLevelList.length;
      }

      if (!isEmpty(taggings.resourceTaggingContentMapList)) {
        const resourceTaggingContentMapModels = taggings.resourceTaggingContentMapList;
        for (const contentMapModel of resourceTaggingContentMapModels) {
          counter += !isEmpty(contentMapModel.taggings)
            ? contentMapModel.taggings.filter((tagging) => isEmpty(tagging.cognitiveLevelId)).length
            : 1;
        }
      }

      if (!isEmpty(taggings.hiddenResourceTaggingContentMapList)) {
        counter += taggings.hiddenResourceTaggingContentMapList.length;
      }

      return counter <= MAXIMUM_RESOURCE_TAGGINGS;
    },
    showMaximumResourceTaggingsWarningPrompt() {
      const props = {
        title: this.$i18n.t("lesson_settings_unable_to_save_title"),
        content: this.$i18n.t("lesson_settings_exceed_maximum_tags_content"),
        primaryBtnLabel: this.$i18n.t("button_ok"),
        use_secondaryButton: false,
        actionPrimary: () => {
          // do nothing
        },
      };
      this.$modal.showMessage("warning", props);
      return;
    },
    async saveAcpModuleResourceTagging(resourceStatus) {
      return await this.saveLessonResourceTaggings({
        uuid: this.lesson.uuid,
        resourceType: "LESSON",
        resourceStatus: resourceStatus,
        params: this.resourceTaggings ? this.resourceTaggings : this.lesson.resourceTaggings,
      });
    },
    async saveAcpSectionResourceTagging() {
      await this.saveLessonActivityResourceTaggings({
        id: this.currentSection.id,
        uuid: this.currentSection.uuid,
        resourceType: "LESSON_ACTIVITY",
        resourceStatus: "APPROVED",
        params: this.resourceTaggings,
      });
    },
    isAaAppAvailable(provider) {
      if (!this.appCategory) return false;
      const authoringCoPilotApp = this.aaAppsBySelectedCategory.find(
        (app) => app.provider === provider,
      );
      if (isEmpty(authoringCoPilotApp)) return false;

      return authoringCoPilotApp.enabled && authoringCoPilotApp.accessible;
    },
    handleGenerateContentChange(value) {
      this.selectedRecommendationIds = value;
    },
    handleGenerateContentSelectAll(value) {
      let selectedIds = [];
      if (value && value.length > 0 && value[0]) {
        // Filter away recommendations which are empty
        selectedIds = this.recommendations
          .filter((i) => i.title || i.content || i.activityDescription || i.activityInstruction)
          .map((i) => {
            return i.id;
          });
      }
      this.selectedRecommendationIds = selectedIds;
    },
    getRecommendationResults(requestId) {
      switch (this.appCategory) {
        case INTEGRATOR_APP_FEATURE_CATEGORY.ACP_MODULE:
          return AuthoringCoPilotApi.getModulePlanRecommendation(requestId);
        case INTEGRATOR_APP_FEATURE_CATEGORY.ACP_ACTIVITY:
        case INTEGRATOR_APP_FEATURE_CATEGORY.ACP_COMPONENT:
        case INTEGRATOR_APP_FEATURE_CATEGORY.ACP_COMPONENT_RE:
          return AuthoringCoPilotApi.getRecommendation({ requestId });
        default:
          return Promise.reject(new Error("Invalid provider"));
      }
    },
    makeRecommendationRequest(requestData) {
      switch (this.appCategory) {
        case INTEGRATOR_APP_FEATURE_CATEGORY.ACP_MODULE: {
          return AuthoringCoPilotApi.postModuleRecommendation(requestData);
        }
        case INTEGRATOR_APP_FEATURE_CATEGORY.ACP_ACTIVITY: {
          return AuthoringCoPilotApi.postActivityRecommendation(requestData);
        }
        case INTEGRATOR_APP_FEATURE_CATEGORY.ACP_COMPONENT: {
          return AuthoringCoPilotApi.postComponentRecommendation(requestData);
        }
        case INTEGRATOR_APP_FEATURE_CATEGORY.ACP_COMPONENT_RE: {
          // TODO: Define the new endpoint
          return AuthoringCoPilotApi.postRegenerateComponentRecommendation(requestData);
        }
        default:
          return Promise.reject(new Error("Invalid provider"));
      }
    },
    showAuthoringCoPilotSubpage(category, id) {
      const title = this.lesson.title;
      switch (category) {
        case INTEGRATOR_APP_FEATURE_CATEGORY.ACP_MODULE:
          {
            const LessonModuleAuthoringCoPilotSubPage = defineAsyncComponent(
              () =>
                import("@modules/editor/components/authoring-copilot/LessonModuleAuthoringCoPilotSubPage.vue"),
            );
            this.$contentSubpage.show(LessonModuleAuthoringCoPilotSubPage, {
              baseTitle: [title],
              authoringCoPilotAppCategory: INTEGRATOR_APP_FEATURE_CATEGORY.ACP_MODULE,
            });
          }
          break;
        case INTEGRATOR_APP_FEATURE_CATEGORY.ACP_COMPONENT:
          {
            const LessonComponentAuthoringCoPilotSubPage = defineAsyncComponent(
              () =>
                import("@modules/editor/components/authoring-copilot/LessonComponentAuthoringCoPilotSubPage.vue"),
            );
            const activityType = this.componentsInQuizActivity
              ? COMPONENT_AUTHORING_COPILOT_TYPE.QUIZ
              : COMPONENT_AUTHORING_COPILOT_TYPE.ACTIVITY;
            this.$contentSubpage.show(LessonComponentAuthoringCoPilotSubPage, {
              baseTitle: [title],
              activityId: id,
              authoringCoPilotAppCategory: INTEGRATOR_APP_FEATURE_CATEGORY.ACP_COMPONENT,
              activityType: activityType,
            });
          }
          break;
        case INTEGRATOR_APP_FEATURE_CATEGORY.ACP_ACTIVITY:
          {
            // For activity
          }
          break;
        case INTEGRATOR_APP_FEATURE_CATEGORY.ACP_COMPONENT_RE:
          {
            const LessonComponentRegenAuthoringCoPilotSubPage = defineAsyncComponent(
              () =>
                import("@modules/editor/components/authoring-copilot/LessonComponentRegenAuthoringCoPilotSubPage.vue"),
            );
            this.$contentSubpage.show(LessonComponentRegenAuthoringCoPilotSubPage, {
              baseTitle: [title],
              componentId: id,
              authoringCoPilotAppCategory: INTEGRATOR_APP_FEATURE_CATEGORY.ACP_COMPONENT_RE,
            });
          }
          break;
        default: {
          return;
        }
      }
    },
    checkResourceTaggingLoaded() {
      // check child settings form field data-loaded (eg: resource taggings)
      const selector = ".resource-taggings[data-loaded]";
      let allLoaded = true;
      document.querySelectorAll(selector).forEach((element) => {
        if (!element.getAttribute("data-loaded")) allLoaded = false;
      });

      if (allLoaded) {
        this.scrollToFirstFormError();
        this.isResourceTaggingLoading = false;
        this.observer.disconnect();
      }
    },
    isFileTypeAllowed(kb: KnowledgeBase, allowedMediaTypes) {
      if (kb.mimeType) {
        return allowedMediaTypes.includes(kb.mimeType);
      }
      return false; // Cannot determine type — block
    },
    validateKnowledgeBaseFiles(selectedKnowledgeBases: KnowledgeBase[], featureType: string) {
      const config = KB_VALIDATION_CONFIG[featureType];
      if (!config) return { valid: true, errorType: null, fileErrors: [] };

      const mediaKbs = selectedKnowledgeBases.filter(
        (kb) =>
          kb.contentType === KnowledgeBaseContentType.MEDIA ||
          kb.contentType === KnowledgeBaseContentType.GOOGLE,
      );

      // STEP 1: Max file count
      if (config.checkMaxFiles && mediaKbs.length > config.maxMediaFiles) {
        return {
          valid: false,
          errorType: "MAX_FILES",
          fileErrors: [],
          bannerMessage: this.$i18n.t("kb_validation_exceed_max_files"),
        };
      }

      // STEP 2: File type check
      const typeErrors: KbFileError[] = [];
      for (const kb of mediaKbs) {
        if (kb.contentType === KnowledgeBaseContentType.GOOGLE) {
          if (!config.allowGoogleFile) {
            typeErrors.push({
              knowledgeBaseId: kb.id,
              errorType: "FILE_TYPE" as const,
              message: this.$i18n.t("kb_validation_unsupported_file_type"),
            });
          }
          continue;
        }
        if (!this.isFileTypeAllowed(kb, config.allowedMediaTypes)) {
          typeErrors.push({
            knowledgeBaseId: kb.id,
            errorType: "FILE_TYPE",
            message: this.$i18n.t("kb_validation_unsupported_file_type"),
          });
        }
      }
      if (typeErrors.length > 0) {
        return {
          valid: false,
          errorType: "FILE_TYPE",
          fileErrors: typeErrors,
        };
      }

      // STEP 3: File size check
      const sizeErrors: KbFileError[] = [];
      let totalSize = 0;
      for (const kb of mediaKbs) {
        if (kb.fileSize == null) continue;
        if (kb.fileSize > config.maxPerFileBytes) {
          sizeErrors.push({
            knowledgeBaseId: kb.id,
            errorType: "PER_FILE_SIZE",
            message: this.$i18n.t("kb_validation_exceed_per_file_size"),
          });
        }
        totalSize += kb.fileSize;
      }
      const totalExceeded = totalSize > config.maxTotalBytes;
      if (sizeErrors.length > 0 || totalExceeded) {
        return {
          valid: false,
          errorType: "FILE_SIZE",
          fileErrors: sizeErrors,
          bannerMessage: totalExceeded
            ? this.$i18n.t("kb_validation_exceed_total_size")
            : undefined,
        };
      }

      return { valid: true, errorType: null, fileErrors: [] };
    },
    validateSelectedKnowledgeBase(selectedKnowledgeBases: KnowledgeBase[], featureType: string) {
      let hasPDF = false;
      let hasGoogleDocument = false;

      selectedKnowledgeBases.forEach((kb) => {
        if (kb.contentType === KnowledgeBaseContentType.GOOGLE) {
          hasGoogleDocument = true;
        } else if (kb.contentType === KnowledgeBaseContentType.MEDIA) {
          if (kb.mediaType === "PDF") {
            hasPDF = true;
          }
        }
      });

      const result = this.validateKnowledgeBaseFiles(selectedKnowledgeBases, featureType);
      this.kbValidationResult = result;
      if (!result.valid) {
        this.showKnowledgeBaseFileValidationError(featureType);
        return false;
      }

      this.isKnowledgeBaseContainPDF = hasPDF;
      this.isKnowledgeBaseContainGoogleDocument = hasGoogleDocument;
      this.showExceedFilesError = false;
      this.kbValidationResult = { valid: true, errorType: null, fileErrors: [] };
      return true;
    },
    showKnowledgeBaseFileValidationError(featureType: string) {
      this.showExceedFilesError = true;
      if (
        featureType === INTEGRATOR_APP_FEATURE_CATEGORY.ACP_MODULE ||
        featureType === INTEGRATOR_APP_FEATURE_CATEGORY.ACP_COMPONENT ||
        featureType === INTEGRATOR_APP_FEATURE_CATEGORY.ACP_ACTIVITY ||
        featureType === INTEGRATOR_APP_FEATURE_CATEGORY.ACP_COMPONENT_RE ||
        featureType === INTEGRATOR_APP_FEATURE_CATEGORY.ACP_HTML5
      ) {
        this.showUnableGenerateErrorToast();
      } else {
        this.showUnableSaveErrorToast();
      }
    },
    showUnableGenerateErrorToast() {
      this.$toast.show("error", {
        title: this.$i18n.t(
          "lesson_activity_authoring_copilot_unable_to_generate_error_toast_title",
        ),
        subTitle: this.$i18n.t(
          "lesson_activity_authoring_copilot_unable_to_generate_or_save_error_toast_message",
        ),
        hasTimeout: true,
      });
    },
    showUnableSaveErrorToast() {
      this.$toast.show("error", {
        title: this.$i18n.t("lesson_activity_authoring_copilot_unable_to_save_error_toast_title"),
        subTitle: this.$i18n.t(
          "lesson_activity_authoring_copilot_unable_to_generate_or_save_error_toast_message",
        ),
        hasTimeout: true,
      });
    },
    showSectionAuthoringCoPilot(id) {
      const LessonActivityAuthoringCoPilotSubPage = defineAsyncComponent(
        () =>
          import("@modules/editor/components/authoring-copilot/LessonActivityAuthoringCoPilotSubPage"),
      );
      this.$contentSubpage.show(LessonActivityAuthoringCoPilotSubPage, {
        baseTitle: [this.sectionData.title],
        id: id,
        authoringCoPilotAppCategory: INTEGRATOR_APP_FEATURE_CATEGORY.ACP_ACTIVITY,
      });
    },
  },
  mounted() {
    const config = {
      attributes: true,
      characterData: true,
      attributeFilter: ["loaded", "data-loaded"],
    };
    document.querySelectorAll(".resource-taggings[data-loaded]").forEach((element) => {
      this.observer.observe(element, config);
    });
  },
  created() {
    this.observer = new MutationObserver(() => {
      this.checkResourceTaggingLoaded();
    });
  },
};
